import { assertNotEmpty } from '@festina-life/core'
import { FPlusCodeContainer } from '@festina-life/fplus'

import { useTemplateContext } from '../../context/templateContext'
import { useLazyLayerMetaModel } from '../../context/useLazyLayerMetaModel'
import type {
  SubTemplateFieldValue,
  SubTemplateValueSetupFieldValue,
  SupportedEditableTemplateFieldValue,
} from '../../types/templateModel'
import { generateEmptyFieldValue } from '../../uiModels/createEmptyFieldHelpers'
import { isInherited } from '../../uiModels/templateOperations/types'
import { InheritedValueCopier } from '../copyInheritedValue/InheritedValueCopier'
import { InlineTextEditor } from '../inlineTextEditor/InlineTextEditor'

type Props = {
  variant: SubTemplateFieldValue
}

export const IdentifiableSubTemplateIdEditor = ({ variant }: Props) => {
  const { templateVersion } = useTemplateContext()
  const metaModel = useLazyLayerMetaModel(templateVersion?.layer?.layerId)

  const handleSaveChanges = (nextIdentifier: string) => {
    const { subTemplatedField } = variant
    assertNotEmpty(subTemplatedField)

    const fieldValue =
      variant.subTemplatedField?.value ??
      (generateEmptyFieldValue({
        field: subTemplatedField,
        meta: metaModel,
      }) as SubTemplateValueSetupFieldValue)

    const allVariants = fieldValue?.variants
    assertNotEmpty(allVariants)

    const nextVariant: SubTemplateFieldValue = {
      ...variant,
      subTemplateIdentifier: nextIdentifier,
      origin: variant.path,
    }

    const nextVariants = allVariants.map((someVariant) =>
      someVariant === variant ? nextVariant : someVariant,
    )

    ;(
      subTemplatedField.update as (
        nextValue: SupportedEditableTemplateFieldValue,
      ) => void
    )({
      ...fieldValue,
      variants: nextVariants,
    })
  }

  const isInheritedValue = isInherited(variant)

  return (
    <FPlusCodeContainer className="group relative">
      <InlineTextEditor
        initialValue={variant.subTemplateIdentifier}
        onSave={handleSaveChanges}
        externalActions={
          isInheritedValue && variant.origin ? (
            <InheritedValueCopier
              onCopy={() => handleSaveChanges(variant.subTemplateIdentifier)}
              fromOrigin={variant.origin}
              fieldPath={variant.path}
            />
          ) : null
        }
      />
    </FPlusCodeContainer>
  )
}
